import { ReactNode } from 'react'

import { FigmaMonochromeIcon } from '@bettermode/icons/social/FigmaMonochrome'
import { GiphyMonochromeIcon } from '@bettermode/icons/social/GiphyMonochrome'
import { GoogleDriveMonochromeIcon } from '@bettermode/icons/social/GoogleDriveMonochrome'
import { TwitterMonochromeIcon } from '@bettermode/icons/social/TwitterMonochrome'
import { YoutubeMonochromeIcon } from '@bettermode/icons/social/YoutubeMonochrome'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { I18nShape } from '../../../i18n/types.js'
import { ComposerExtensionConfig } from '../../@types/index.js'

export enum MenuTypes {
  BulletList = 'bulletList',
  Blockquote = 'blockquote',
  NumberedList = 'numberedList',
  LargeHeader = 'largeHeader',
  MediumHeader = 'mediumHeader',
  Image = 'image',
  Attachment = 'attachment',
  Emoji = 'emoji',
  Mention = 'mention',
  Embed = 'embed',
  Link = 'link',
  CodeBlock = 'codeBlock',
  Video = 'video',
  Audio = 'audio',
  Giphy = 'giphy',
  Table = 'table',
  TableOfContents = 'tableOfContents',
  Poll = 'poll',
}

export type MenuItem = {
  type?: `${MenuTypes}` | 'divider'
  label?: string
  icon?: ReactNode
  description?: string
}

export type MenuItemContext = {
  canCreatePoll?: boolean
}

export const slashMenuItemsFactory =
  (intl: I18nShape) =>
  (extensions: ComposerExtensionConfig, context?: MenuItemContext) => {
    const items: MenuItem[] = []
    const { $t } = intl
    if (extensions.heading) {
      items.push(
        {
          type: MenuTypes.LargeHeader,
          label: $t({
            id: 'Composer.Heading2',
            defaultMessage: 'Heading 2',
          }),
          icon: <SvgIcon name="h-2" />,
        },
        {
          type: MenuTypes.MediumHeader,
          label: $t({
            id: 'Composer.Heading3',
            defaultMessage: 'Heading 3',
          }),
          icon: <SvgIcon name="h-3" />,
        },
      )
    }

    items.push(
      {
        type: MenuTypes.Blockquote,
        label: $t({
          id: 'Composer.Blockquote',
          defaultMessage: 'Blockquote',
        }),
        icon: <SvgIcon name="double-quotes-l" />,
      },
      {
        type: MenuTypes.BulletList,
        label: $t({
          id: 'Composer.SlashMenu.BulletedLIst',
          defaultMessage: 'Bulleted List',
        }),
        icon: <SvgIcon name="list-unordered" />,
      },
      {
        type: MenuTypes.NumberedList,
        label: $t({
          id: 'Composer.SlashMenu.NumberedList',
          defaultMessage: 'Numbered List',
        }),
        icon: <SvgIcon name="list-ordered" />,
      },
    )

    if (extensions.code) {
      items.push({
        type: MenuTypes.CodeBlock,
        label: $t({
          id: 'Composer.CodeBlock',
          defaultMessage: 'Code Block',
        }),
        icon: <SvgIcon name="code-box" />,
      })
    }

    if (extensions.table) {
      items.push({
        type: MenuTypes.Table,
        label: $t({
          id: 'Generics.Table',
          defaultMessage: 'Table',
        }),
        icon: <SvgIcon name="layout-quadrants" />,
      })
    }

    if (extensions.tableOfContents) {
      items.push({
        type: MenuTypes.TableOfContents,
        label: $t({
          id: 'Composer.TableOfContents',
          defaultMessage: 'Table of contents',
        }),
        icon: <SvgIcon name="book-open" />,
      })
    }

    items.push(
      {
        type: 'divider',
      },
      {
        type: MenuTypes.Link,
        label: $t({
          id: 'Generics.Link.Noun',
          defaultMessage: 'Link',
        }),
        icon: <SvgIcon name="link" />,
      },
    )

    if (extensions.mention) {
      items.push({
        type: MenuTypes.Mention,
        label: $t({
          id: 'Composer.SlashMenu.Mention',
          defaultMessage: 'Mention',
        }),
        icon: <SvgIcon name="at-symbol" />,
      })
    }

    if (extensions.emoji) {
      items.push({
        type: MenuTypes.Emoji,
        label: $t({
          id: 'Generics.Emoji',
          defaultMessage: 'Emoji',
        }),
        icon: <SvgIcon name="emotion" />,
      })
    }

    items.push({
      type: 'divider',
    })

    if (extensions.attachment) {
      items.push({
        type: MenuTypes.Attachment,
        label: $t({
          id: 'Composer.SlashMenu.Attachment',
          defaultMessage: 'Attachment',
        }),
        icon: <SvgIcon name="attachment" />,
      })
    }

    if (extensions.image) {
      items.push({
        type: MenuTypes.Image,
        label: $t({
          id: 'Generics.Image',
          defaultMessage: 'Image',
        }),
        icon: <SvgIcon name="image" />,
      })
    }

    if (extensions.video) {
      items.push(
        {
          type: MenuTypes.Video,
          label: $t({
            id: 'Composer.SlashMenu.Video',
            defaultMessage: 'Video',
          }),
          icon: <SvgIcon name="video" />,
        },
        {
          type: MenuTypes.Audio,
          label: $t({
            id: 'Composer.SlashMenu.Audio',
            defaultMessage: 'Audio',
          }),
          icon: <SvgIcon name="play-circle" />,
        },
      )
    }

    if (extensions.poll && context?.canCreatePoll) {
      items.push({
        type: MenuTypes.Poll,
        label: $t({
          id: 'Generics.Poll',
          defaultMessage: 'Poll',
        }),
        icon: <SvgIcon name="chart" />,
      })
    }

    if (extensions.embed) {
      items.push(
        {
          type: MenuTypes.Embed,
          label: $t({
            id: 'Generics.Embed',
            defaultMessage: 'Embed',
          }),
          icon: <SvgIcon name="safari-line" />,
        },
        {
          type: 'divider',
        },
      )

      if (extensions.image) {
        items.push({
          type: MenuTypes.Giphy,
          label: 'Giphy',
          icon: <GiphyMonochromeIcon />,
        })
      }

      items.push(
        {
          type: MenuTypes.Embed,
          description: $t({
            id: 'Composer.SlashMenu.YoutubeEmbedDescription',
            defaultMessage: 'Embed a Youtube Video.',
          }),
          label: $t({
            id: 'Composer.SlashMenu.Youtube',
            defaultMessage: 'Youtube',
          }),
          icon: <YoutubeMonochromeIcon />,
        },
        {
          type: MenuTypes.Embed,
          description: $t({
            id: 'Composer.SlashMenu.XEmbedDescription',
            defaultMessage: 'Embed an X post.',
          }),
          label: $t({
            id: 'Composer.SlashMenu.X',
            defaultMessage: 'X',
          }),
          icon: <TwitterMonochromeIcon />,
        },
        {
          type: MenuTypes.Embed,
          description: $t({
            id: 'Composer.SlashMenu.GoogleDriveEmbedDescription',
            defaultMessage: 'Embed a Google Doc, Google Sheet...',
          }),
          label: $t({
            id: 'Composer.SlashMenu.GoogleDrive',
            defaultMessage: 'Google Drive',
          }),
          icon: <GoogleDriveMonochromeIcon />,
        },
        {
          type: MenuTypes.Embed,
          description: $t({
            id: 'Composer.SlashMenu.FigmaEmbedDescription',
            defaultMessage: 'Embed a Figma file.',
          }),
          label: $t({
            id: 'Composer.SlashMenu.Figma',
            defaultMessage: 'Figma',
          }),
          icon: <FigmaMonochromeIcon />,
        },
      )
    }

    return items
  }
